import { useState } from 'react';
import type { MouseEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { classPath, profilePath } from '@/app/routes/paths';
import { isCurrentUserClassOwner } from '@/features/class/classAccess';
import { registerBoard } from '@/features/class/classRegistry';
import {
  BOARD_REQUEST_JOINED,
  BOARD_REQUEST_NOT_JOINED,
  BOARD_REQUEST_PENDING,
  toggleBoardMembership,
  type BoardMembershipStatus,
} from '@/features/class/boardsApi';
import { useClassMembershipStore } from '@/features/class/classMembershipStore';
import { useClassRequestsStore } from '@/features/notifications/classRequestsStore';
import { MOCK_CURRENT_USER_PROFILE } from '@/features/profile/mockData';
import type { SearchClassResult } from '@/features/search/types';
import Button from '@/shared/ui/Button';
import { LockIcon } from '@/shared/icons';
import { textStyles } from '@/shared/tokens/typography';
import { cn } from '@/shared/lib/cn';
import { useAuthStore } from '@/store/authStore';

interface SearchClassResultRowProps {
  classItem: SearchClassResult;
  /** Closes the search panel (profile navigation, open class). */
  onNavigate: () => void;
}

export default function SearchClassResultRow({ classItem, onNavigate }: SearchClassResultRowProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const authUserId = useAuthStore((state) => state.user?.id ?? null);
  const authUsername = useAuthStore((state) => state.user?.username ?? '');
  const [localJoinStatus, setLocalJoinStatus] = useState<BoardMembershipStatus | null>(null);
  const storeIsJoined = useClassMembershipStore((state) => state.isJoined(classItem.classId));
  const storeHasRequested = useClassMembershipStore((state) =>
    state.hasRequested(classItem.classId),
  );
  const setMembershipStatus = useClassMembershipStore((state) => state.setMembershipStatus);
  const submitJoinRequest = useClassRequestsStore((state) => state.submitJoinRequest);
  const withdrawJoinRequest = useClassRequestsStore((state) => state.withdrawJoinRequest);

  const apiJoinStatus = localJoinStatus ?? classItem.isJoined;
  const fallbackJoinStatus = storeIsJoined
    ? BOARD_REQUEST_JOINED
    : storeHasRequested
      ? BOARD_REQUEST_PENDING
      : BOARD_REQUEST_NOT_JOINED;
  const membershipStatus = apiJoinStatus ?? fallbackJoinStatus;
  const isJoined = membershipStatus === BOARD_REQUEST_JOINED;
  const hasRequested = membershipStatus === BOARD_REQUEST_PENDING;

  const isOwnClass =
    (authUserId != null && classItem.ownerUserId === String(authUserId)) ||
    (authUsername.length > 0 &&
      classItem.ownerUsername.toLowerCase() === authUsername.toLowerCase());
  const isOwner = isOwnClass || isCurrentUserClassOwner(classItem.code);
  const canOpenClass = isOwner || isJoined;

  const membershipMutation = useMutation({
    mutationFn: toggleBoardMembership,
    onSuccess: (result) => {
      const nextStatus = result.isJoined ?? BOARD_REQUEST_NOT_JOINED;
      setLocalJoinStatus(nextStatus);
      setMembershipStatus(classItem.classId, nextStatus);

      if (nextStatus === BOARD_REQUEST_PENDING) {
        submitJoinRequest({
          classId: classItem.classId,
          classCode: classItem.code,
          className: classItem.name,
          ownerUsername: classItem.ownerUsername,
          requesterName: MOCK_CURRENT_USER_PROFILE.name,
          requesterUsername: MOCK_CURRENT_USER_PROFILE.username,
          requesterInitials: MOCK_CURRENT_USER_PROFILE.initials,
        });
      } else {
        withdrawJoinRequest(classItem.classId, MOCK_CURRENT_USER_PROFILE.username);
      }

      void queryClient.invalidateQueries({ queryKey: ['search'] });
      void queryClient.invalidateQueries({ queryKey: ['boards', 'joined'] });
      void queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });

  const goToOwnerProfile = () => {
    navigate(profilePath(classItem.ownerUsername));
    onNavigate();
  };

  const handleOpenClass = (event: MouseEvent) => {
    event.stopPropagation();
    if (!canOpenClass) {
      goToOwnerProfile();
      return;
    }
    registerBoard({
      id: classItem.classId,
      code: classItem.code,
      name: classItem.name,
      isPrivate: classItem.isPrivate,
    });
    navigate(classPath(classItem.classId), {
      state: { classListContext: isOwner ? 'owned' : 'joined' },
    });
    onNavigate();
  };

  const handleMembershipToggle = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (membershipMutation.isPending || !authUserId) {
      return;
    }

    membershipMutation.mutate({
      boardId: classItem.classId,
      joinedBy: authUserId,
      currentStatus: membershipStatus,
    });
  };

  const renderAction = () => {
    if (isOwner || isJoined) {
      return (
        <Button type="button" variant="secondary" size="sm" onClick={handleOpenClass}>
          Open class
        </Button>
      );
    }
    if (hasRequested) {
      return (
        <div className="flex flex-col gap-1.5 sm:flex-row">
          <Button type="button" variant="secondary" size="sm" disabled className="min-w-[5.5rem]">
            Requested
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleMembershipToggle}
            disabled={membershipMutation.isPending || !authUserId}
          >
            Cancel
          </Button>
        </div>
      );
    }
    return (
      <Button
        type="button"
        variant="primary"
        size="sm"
        onClick={handleMembershipToggle}
        disabled={membershipMutation.isPending || !authUserId}
      >
        {membershipMutation.isPending ? 'Updating…' : 'Join'}
      </Button>
    );
  };

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <span
        className={cn('mt-1.5 h-3 w-3 shrink-0 rounded-full', classItem.dotClassName)}
        aria-hidden
      />

      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={goToOwnerProfile}
          className="w-full text-left transition-colors hover:text-brand-emphasis"
        >
          <p className="truncate font-medium text-gray-900">{classItem.name}</p>
          <p className={cn(textStyles.caption, 'tabular-nums')}>{classItem.code}</p>
          <p className={cn(textStyles.caption, 'mt-1 text-gray-500')}>
            Owner {classItem.ownerName}
            {classItem.isPrivate ? (
              <span className="ml-1.5 inline-flex items-center gap-0.5 text-gray-400">
                <LockIcon className="h-3 w-3" aria-hidden />
                Private
              </span>
            ) : null}
          </p>
        </button>
      </div>

      <div className="flex shrink-0 flex-col gap-1.5 sm:flex-row sm:items-center">
        {renderAction()}
      </div>
    </div>
  );
}
